/**
 * TSGo company / support pages.
 *
 * One file, seven small default-exports so we don't scatter tiny sibling
 * files that all share the same layout. Each page is a plain content
 * section wrapped in the standard AppLayout (they're registered as
 * children of AppLayout inside routes/AppRoutes.tsx).
 */

import { Instagram, MessageCircle, MapPin, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const INSTAGRAM = "https://www.instagram.com/tsgo.official";
const WHATSAPP = "https://wa.me/917330827980";
const HQ = "Paralakhemundi, Odisha, India";

function PageShell({
    title,
    subtitle,
    children,
    testId,
}: {
    title: string;
    subtitle?: string;
    children: React.ReactNode;
    testId: string;
}) {
    return (
        <div data-testid={testId} className="mx-auto max-w-3xl px-4 py-10">
            <header className="mb-8">
                <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">{title}</h1>
                {subtitle ? (
                    <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
                ) : null}
            </header>
            <div className="prose prose-neutral max-w-none text-sm leading-relaxed text-foreground">
                {children}
            </div>
        </div>
    );
}

export function AboutPage() {
    return (
        <PageShell
            title="About TSGo"
            subtitle="Hyperlocal Commerce & Technology Company"
            testId="page-about"
        >
            <p>
                <strong>TSGo</strong> is a hyperlocal commerce and technology company
                founded by <strong>Ananth Ram Das</strong> and headquartered in {HQ}. We
                connect neighbourhoods with the goods they need — delivered fast,
                priced fairly, and backed by tech that treats speed and reliability
                as first-class citizens.
            </p>
            <h2>Our Mission</h2>
            <p>
                To make everyday shopping faster, smarter and more convenient through
                technology.
            </p>
            <h2>Our Vision</h2>
            <p>To become India's most trusted hyperlocal commerce platform.</p>
            <h2>Core Values</h2>
            <ul>
                <li><strong>Trust</strong> — every transaction, honoured.</li>
                <li><strong>Innovation</strong> — new tools for old problems.</li>
                <li><strong>Quality</strong> — never compromised.</li>
                <li><strong>Speed</strong> — minutes matter.</li>
                <li><strong>Reliability</strong> — show up, every time.</li>
                <li><strong>Customer Satisfaction</strong> — the only metric that counts.</li>
            </ul>
            <p>
                <Link to="/careers" className="font-semibold underline">Careers</Link>{" · "}
                <Link to="/press" className="font-semibold underline">Press</Link>{" · "}
                <Link to="/contact" className="font-semibold underline">Contact</Link>
            </p>
        </PageShell>
    );
}

export function CareersPage() {
    return (
        <PageShell
            title="Careers at TSGo"
            subtitle="Join TSGo and help build the future of hyperlocal commerce."
            testId="page-careers"
        >
            <div className="rounded-2xl border border-dashed border-border/70 bg-white p-8 text-center">
                <p className="text-lg font-bold">We're Hiring Soon.</p>
                <p className="mt-2 text-sm text-muted-foreground">
                    Roles across engineering, operations, and customer experience
                    are opening up. Follow us on{" "}
                    <a href={INSTAGRAM} target="_blank" rel="noreferrer" className="font-semibold underline">
                        @tsgo.official
                    </a>{" "}
                    for the first announcements.
                </p>
            </div>
        </PageShell>
    );
}

export function PressPage() {
    return (
        <PageShell
            title="Press"
            subtitle="Official company news, announcements and media updates."
            testId="page-press"
        >
            <p>
                For interviews, features, or media enquiries, reach us via WhatsApp
                at <a href={WHATSAPP} className="font-semibold underline">+91 7330827980</a>
                {" "}or on Instagram{" "}
                <a href={INSTAGRAM} target="_blank" rel="noreferrer" className="font-semibold underline">@tsgo.official</a>.
            </p>
            <p>Press releases and announcements will appear here as we grow.</p>
        </PageShell>
    );
}

export function ContactPage() {
    return (
        <PageShell title="Contact" testId="page-contact">
            <ul className="not-prose space-y-4">
                <li className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-4 w-4 text-brand" />
                    <div>
                        <p className="font-semibold">Headquarters</p>
                        <p className="text-sm text-muted-foreground">{HQ}</p>
                    </div>
                </li>
                <li className="flex items-start gap-3">
                    <MessageCircle className="mt-0.5 h-4 w-4 text-brand" />
                    <div>
                        <p className="font-semibold">WhatsApp Support</p>
                        <a
                            href={WHATSAPP}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-muted-foreground underline"
                        >
                            +91 7330827980
                        </a>
                    </div>
                </li>
                <li className="flex items-start gap-3">
                    <Instagram className="mt-0.5 h-4 w-4 text-brand" />
                    <div>
                        <p className="font-semibold">Instagram</p>
                        <a
                            href={INSTAGRAM}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-muted-foreground underline"
                        >
                            @tsgo.official
                        </a>
                    </div>
                </li>
            </ul>
        </PageShell>
    );
}

export function HelpCenterPage() {
    const faqs = [
        {
            q: "How long does delivery take?",
            a: "Most orders reach you in 15–30 minutes. Exact ETA is shown at checkout and updates live on the tracking page.",
        },
        {
            q: "What payment methods do you accept?",
            a: "TSGo Version 1.0 supports Cash on Delivery and TSGo Wallet. Online gateways will return in a future release.",
        },
        {
            q: "How do I add money to my TSGo Wallet?",
            a: "Open the Wallet page and tap the WhatsApp button. Our team will verify your transfer and credit your wallet.",
        },
        {
            q: "How do I track my order?",
            a: "Go to Orders → tap any order → see live rider location, chat, and OTP handover.",
        },
        {
            q: "How do I return a product?",
            a: "Refuse the item at the door or write to us on WhatsApp within 24 hours. Refunds go back to your TSGo Wallet.",
        },
        {
            q: "The rider hasn't arrived — what should I do?",
            a: "Message the rider from the order page. If unresponsive, ping our WhatsApp support and we'll intervene.",
        },
    ];
    return (
        <PageShell
            title="Help Center"
            subtitle="Answers to the questions we hear most often."
            testId="page-help"
        >
            <div className="not-prose space-y-3">
                {faqs.map((f) => (
                    <details
                        key={f.q}
                        className="group rounded-xl border border-border/60 bg-white p-4 open:shadow-sm"
                    >
                        <summary className="cursor-pointer list-none text-sm font-semibold">
                            {f.q}
                        </summary>
                        <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
                    </details>
                ))}
            </div>
            <div className="not-prose mt-8 rounded-2xl border border-border/60 bg-white p-5">
                <p className="text-sm font-bold">Still stuck?</p>
                <p className="mt-1 text-xs text-muted-foreground">
                    Our support team is on WhatsApp — reply within minutes during
                    business hours.
                </p>
                <a
                    href={WHATSAPP}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-bold text-white"
                >
                    <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
                </a>
                <p className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-1">
                    <Mail className="h-3 w-3" /> Prefer email? Message us on Instagram DM.
                </p>
            </div>
        </PageShell>
    );
}

export function TermsPage() {
    return (
        <PageShell
            title="Terms & Conditions"
            subtitle="Last updated: February 2026"
            testId="page-terms"
        >
            <p>
                Welcome to TSGo. By using our app or website, you agree to these
                terms. Please read them carefully.
            </p>
            <h2>1. Service</h2>
            <p>
                TSGo is a hyperlocal commerce platform operated by TSGo, headquartered
                in {HQ}. We facilitate the delivery of goods from local stores to
                customers within our service radius.
            </p>
            <h2>2. Accounts</h2>
            <p>
                You are responsible for maintaining the confidentiality of your
                account credentials and for all activity under your account.
            </p>
            <h2>3. Orders &amp; Payments</h2>
            <p>
                Version 1.0 supports Cash on Delivery and the TSGo Wallet. Prices,
                delivery fees and availability are shown at checkout and may change
                without notice.
            </p>
            <h2>4. Refunds</h2>
            <p>
                Refunds for eligible cancellations or returns are credited to your
                TSGo Wallet unless otherwise arranged over WhatsApp support.
            </p>
            <h2>5. Prohibited Use</h2>
            <p>
                You may not use TSGo for any unlawful purpose, to abuse riders or
                staff, or to attempt to reverse-engineer our systems.
            </p>
            <h2>6. Contact</h2>
            <p>
                For any question about these terms, reach us via WhatsApp at{" "}
                <a href={WHATSAPP} className="font-semibold underline">+91 7330827980</a>.
            </p>
        </PageShell>
    );
}

export function PrivacyPage() {
    return (
        <PageShell
            title="Privacy Policy"
            subtitle="Last updated: February 2026"
            testId="page-privacy"
        >
            <p>
                Your privacy is important to us. This policy explains what data we
                collect, why we collect it, and how we protect it.
            </p>
            <h2>1. Data We Collect</h2>
            <ul>
                <li>Account details you provide (name, phone, email).</li>
                <li>Delivery address and GPS coordinates you share for order routing.</li>
                <li>Order history, payment method (never card numbers — we don't store them).</li>
                <li>Device information required to deliver push notifications.</li>
            </ul>
            <h2>2. How We Use It</h2>
            <p>
                Solely to operate TSGo — routing orders, notifying you of status
                changes, improving reliability, and preventing fraud.
            </p>
            <h2>3. Sharing</h2>
            <p>
                We share the minimum required data with the rider assigned to your
                order (name, phone, address). We never sell your data.
            </p>
            <h2>4. Security</h2>
            <p>
                Passwords are hashed with bcrypt. Sessions use httpOnly cookies over
                HTTPS. Sensitive operations require re-authentication.
            </p>
            <h2>5. Your Rights</h2>
            <p>
                Request account or data deletion any time via WhatsApp at{" "}
                <a href={WHATSAPP} className="font-semibold underline">+91 7330827980</a>.
            </p>
        </PageShell>
    );
}
